import React, { useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import BottomSheet from "@/components/BottomSheet";
import { Input } from "@/components/Input";
import { AppText } from "@/components/AppText";
import { FontFamilies } from "@/constants/Typography";
import { Colors } from "@/constants/Colors";

type Props = {
  visible: boolean;
  onConfirm: (pin: string) => void;
  onSkip: () => void;
  onClose: () => void;
};

const PinConfirmationModal: React.FC<Props> = ({
  visible,
  onConfirm,
  onSkip,
  onClose,
}) => {
  const [pin, setPin] = useState("");

  const handleConfirm = () => {
    onConfirm(pin);
    setPin(""); // Clear PIN after confirmation
  };

  const handleSkip = () => {
    setPin(""); // Clear PIN when skipping
    onSkip();
  };

  const handleClose = () => {
    setPin(""); // Clear PIN when closing
    onClose();
  };

  return (
    <BottomSheet
      isVisible={visible}
      onClose={handleClose}
      title="Account PIN (Optional)"
      height={420}
      enableKeyboardAvoidance={true}
      enableBackdropDismiss={true}
      enableSwipeDown={true}
    >
      <View style={styles.content}>
        {/* Description */}
        <AppText style={styles.description}>
          If your account has a PIN for verification, enter it here. This helps
          our AI agent verify your identity during the call.
        </AppText>

        {/* PIN Input */}
        <Input
          label="Account PIN"
          placeholder="Enter PIN"
          keyboardType="numeric"
          secureTextEntry={true}
          maxLength={10}
          value={pin}
          onChangeText={setPin}
          autoFocus={true}
          containerStyle={styles.inputContainer}
        />

        {/* Confirm Button */}
        <TouchableOpacity
          style={[styles.confirmButton, !pin && styles.buttonDisabled]}
          onPress={handleConfirm}
          disabled={!pin}
        >
          <AppText style={styles.confirmButtonText}>Confirm</AppText>
        </TouchableOpacity>

        {/* Skip Button */}
        <TouchableOpacity style={styles.skipButton} onPress={handleSkip}>
          <AppText style={styles.skipButtonText}>
            Skip - I don&apos;t have a PIN
          </AppText>
        </TouchableOpacity>
      </View>
    </BottomSheet>
  );
};

export default PinConfirmationModal;

const styles = StyleSheet.create({
  content: {
    flex: 1,
    paddingTop: 8,
    gap: 16,
  },
  description: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 14,
    lineHeight: 22,
    color: "rgba(22, 29, 20, 0.7)",
    marginBottom: 8,
  },
  inputContainer: {
    marginBottom: 8,
  },
  confirmButton: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
    height: 56,
    marginTop: 8,
  },
  buttonDisabled: {
    backgroundColor: "#A8D5B1",
    opacity: 0.6,
  },
  confirmButtonText: {
    fontFamily: FontFamilies.Inter.Bold,
    fontSize: 17,
    color: "#FFFFFF",
  },
  skipButton: {
    backgroundColor: "#F4F5F6",
    borderRadius: 100,
    paddingVertical: 16,
    alignItems: "center",
    justifyContent: "center",
    height: 56,
  },
  skipButtonText: {
    fontFamily: FontFamilies.Inter.Bold,
    fontSize: 17,
    color: "#A3A3A3",
  },
});
